import algoliasearch, { AlgoliaSearchOptions, SearchClient } from 'algoliasearch/lite'
import shuffle from 'lodash/shuffle'
import { reportErrorToBugsnag } from '@/utils/bugsnag'

export const ALGOLIA_APP_ID = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID as string
export const ALGOLIA_READ_ONLY_KEY = process.env.NEXT_PUBLIC_ALGOLIA_READ_ONLY_KEY as string

interface AlgoliaClientOptions {
  /** By default, we will not run a search when the query is empty. If you want to run a search when the query is empty, set performEmptySearch to true. */
  performEmptySearch?: boolean
}

export async function getAlgoliaSearchClient(options?: AlgoliaClientOptions): Promise<SearchClient> {
  const performEmptySearch = options?.performEmptySearch ?? false

  const hosts = await getAlgoliaHosts()

  const algoliaOptions: AlgoliaSearchOptions = {
    headers: {
      'x-algolia-application-id': ALGOLIA_APP_ID,
      'x-algolia-api-key': ALGOLIA_READ_ONLY_KEY,
      // Worked in staging but not prod.
      // 'x-client-name': 'angel-web',
    },
    hosts,
  }

  const algoliaClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_READ_ONLY_KEY, algoliaOptions)
  if (performEmptySearch) return algoliaClient

  return {
    ...algoliaClient,
    async search(requests) {
      const isEmptyQuery = requests.every(({ params }) => !params?.query)
      if (isEmptyQuery) {
        return {
          results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: false,
            query: '',
            params: '',
          })),
        }
      }

      return algoliaClient.search(requests)
    },
  } as SearchClient
}

async function getAlgoliaHosts(): Promise<string[]> {
  try {
    const str = process.env.NEXT_PUBLIC_ALGOLIA_HOSTS

    if (!str) {
      throw new Error('NEXT_PUBLIC_ALGOLIA_HOSTS env var not defined')
    }

    const json = JSON.parse(str)
    if (!Array.isArray(json)) {
      throw new Error(`expected NEXT_PUBLIC_ALGOLIA_HOSTS to be a JSON array but instead got ${str}`)
    }

    if (json.some((x) => typeof x !== 'string')) {
      throw new Error(`expected NEXT_PUBLIC_ALGOLIA_HOSTS to be an array of strings but instead got ${str}`)
    }

    return shuffle(json)
  } catch (err) {
    reportErrorToBugsnag(err as Error)

    // If env var is not defined, used Algolia's recommended default set of hosts.
    return shuffle([
      `${ALGOLIA_APP_ID}-dsn.algolia.net`,
      `${ALGOLIA_APP_ID}-1.algolianet.com`,
      `${ALGOLIA_APP_ID}-2.algolianet.com`,
      `${ALGOLIA_APP_ID}-3.algolianet.com`,
    ])
  }
}
